import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  structuredData?: object;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Free QR Code Generator — Create QR Codes Instantly',
  description = 'Create free QR codes instantly for URLs, text, email, phone, SMS, WiFi, vCard, and events. Download as PNG or SVG. No login required. Fast, secure, and privacy-first QR code generator.',
  keywords = 'free qr code, qr code generator, create qr code, qr code maker, online qr generator, wifi qr code, vcard qr code, sms qr code',
  canonical,
  ogImage = 'https://freeqrcodenow.com/og-image.png',
  structuredData,
}) => {
  const location = useLocation();
  const currentUrl = `https://freeqrcodenow.com${location.pathname}`;
  const canonicalUrl = canonical || currentUrl;

  useEffect(() => {
    document.title = title;

    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Standard meta tags
    updateMeta('description', description);
    updateMeta('keywords', keywords);

    // Open Graph tags
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:type', 'website', true);
    updateMeta('og:site_name', 'FreeQRCodeNow', true);

    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', ogImage);

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // Structured data
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script') as HTMLScriptElement;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, canonicalUrl, ogImage, structuredData]);

  return null;
};

// Structured Data Schemas
export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do I create a free QR code?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Simply select your QR code type (URL, text, email, phone, SMS, WiFi, vCard, or event), enter your content, customize the design if desired, and click download. No registration required!',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add a logo to my QR code?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! You can upload your logo and it will be automatically scaled to ensure your QR code remains scannable. We use high error correction to maintain scannability.',
      },
    },
    {
      '@type': 'Question',
      name: 'What formats can I download QR codes in?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can download QR codes in PNG format with optional transparent backgrounds, or as SVG for vector graphics that scale perfectly at any size.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are QR codes free forever?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! All static QR codes are completely free with no expiration. Create unlimited QR codes without any registration or payment.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is it safe to use this QR code generator?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely! We don\'t store your data, and all QR code generation happens in your browser for maximum privacy and security.',
      },
    },
  ],
};

export const breadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FreeQRCodeNow',
  url: 'https://freeqrcodenow.com',
  logo: 'https://freeqrcodenow.com/logo512.png',
  description: 'Free QR code generator for creating customizable QR codes instantly.',
  sameAs: [
    // Add your social media links here
  ],
};

